import * as React from 'react';
import { useIntl } from '@/intl';
import { Dot, Table, tk, Typography } from '@sunwebgroup/ui-components';
import { css, styled } from 'styled-components';
import type { Price } from '@/domain/prices/prices/types';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createPricesViewedClickEvent } from '@/domain/prices/analytics';

const StyledPriceCell = styled(Table.Cell)<{
  $hovered: boolean;
  $horizontallyHighlighted: boolean;
  $verticallyHighlighted: boolean;
  $hasPrice: boolean;
  $selected: boolean;
  $isBestValue: boolean;
}>`
  ${Dot} {
    position: absolute;
    top: ${tk('spacing_2xs')};
    right: ${tk('spacing_2xs')};
  }

  ${({ $hasPrice }) =>
    $hasPrice &&
    css`
      cursor: pointer;
    `}

  ${({ $horizontallyHighlighted, $hovered }) =>
    $horizontallyHighlighted &&
    !$hovered &&
    css`
      &:before {
        border-top: 1px solid ${tk('color_semantic_border_strong')};
        border-bottom: 1px solid ${tk('color_semantic_border_strong')};
      }
    `}

  ${({ $verticallyHighlighted, $hovered }) =>
    $verticallyHighlighted &&
    !$hovered &&
    css`
      border-right-color: ${tk('color_semantic_border_strong')};

      &:before {
        border-left: 1px solid ${tk('color_semantic_border_strong')};
        border-right: 1px solid ${tk('color_semantic_border_strong')};
      }
    `}

  ${({ $hovered, $selected }) =>
    ($hovered || $selected) &&
    css`
      background-color: ${tk('color_semantic_bg_strong')};
      border-color: ${tk('color_semantic_border_strong')};

      &:before {
        border-left: 1px solid ${tk('color_semantic_border_strong')};
        border-top: 1px solid ${tk('color_semantic_border_strong')};
      }
    `}

  ${({ $selected, $isBestValue }) =>
    ($selected || $isBestValue) &&
    css`
      ${Typography} {
        ${Typography.modVariant('body-md-bold')}
      }
    `}

  ${({ $isBestValue, $selected, $hovered }) =>
    $isBestValue &&
    !$selected &&
    !$hovered &&
    css`
      ${Typography} {
        color: ${tk('color_semantic_icon_success')};
      }
    `}
`;

export const PriceCell = React.memo(
  ({
    price,
    onHoverStart,
    onHoverEnd,
    horizontallyHighlighted,
    verticallyHighlighted,
    isHovered,
    onClick,
    isSelected,
  }: {
    price: Price | undefined;
    onHoverStart: (duration: string, date: string) => void;
    onHoverEnd: () => void;
    isHovered: boolean;
    horizontallyHighlighted: boolean;
    verticallyHighlighted: boolean;
    onClick: (priceId: string) => void;
    isSelected: boolean;
  }) => {
    const intl = useIntl();

    const { sendEvent } = useAnalytics();

    const id = price?.id ?? '';
    const duration = price?.duration.toString() ?? '';
    const date = price?.departureDate ?? '';

    const onMouseEnterHandler = React.useCallback(() => {
      if (price) onHoverStart(duration, date);
    }, [onHoverStart, price, duration, date]);

    const onMouseLeaveHandler = React.useCallback(() => {
      onHoverEnd();
    }, [onHoverEnd]);

    const onClickHandler = React.useCallback(() => {
      if (price) {
        sendEvent(
          createPricesViewedClickEvent({
            price: price.minPricePerPerson,
            isHighlighted: price.isBestValue,
          })
        );
        onClick(id);
      }
    }, [onClick, price, id]);

    //TODO review if the Match.ceil is correct to be done here
    const priceText = price ? intl.formatTruncatedCurrency(Math.ceil(price.minPricePerPerson)) : '-';
    return (
      <StyledPriceCell
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        $hovered={isHovered}
        $horizontallyHighlighted={horizontallyHighlighted}
        $verticallyHighlighted={verticallyHighlighted}
        $hasPrice={Boolean(price)}
        onClick={onClickHandler}
        $selected={isSelected}
        $isBestValue={price?.isBestValue ?? false}
        data-qa-price-value={priceText}
      >
        {price?.bullet && <Dot $color={price?.bullet.hexColor} />}
        <Typography $variant="body-md">{priceText}</Typography>
      </StyledPriceCell>
    );
  }
);

PriceCell.displayName = 'PriceCell';
